import React from 'react';
import {
  BookOpen, Rocket, Bluetooth, Code, Database, Monitor,
  ChevronRight
} from 'lucide-react';
import { useLanguageStore } from '../../stores/languageStore';
import { getDocumentStructure, documentTitles } from '../../docs/structure';
import type { DocumentSection, DocumentSubsection } from './index';

interface DocumentsSidebarProps {
  activeSection: DocumentSection;
  activeSubsection: DocumentSubsection;
  onSectionChange: (section: DocumentSection) => void;
  onSubsectionChange: (subsection: DocumentSubsection) => void;
}

const sectionIcons: Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  'overview': BookOpen,
  'getting-started': Rocket,
  'ble-protocol': Bluetooth,
  'api-reference': Code,
  'data-guide': Database,
  'interface': Monitor,
};

export const DocumentsSidebar: React.FC<DocumentsSidebarProps> = ({
  activeSection,
  activeSubsection,
  onSectionChange,
  onSubsectionChange,
}) => {
  const { currentLanguage } = useLanguageStore();
  const titles = documentTitles[currentLanguage as keyof typeof documentTitles] as Record<string, string>;
  const documentStructure = getDocumentStructure(currentLanguage);

  const sections = documentStructure.map(section => ({
    id: section.id as DocumentSection,
    label: titles[section.id] || section.title,
    subsections: section.subsections.map(sub => ({
      id: sub.id,
      label: titles[sub.id] || sub.title,
      filePath: sub.filePath,
    }))
  }));

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="p-5 border-b border-white/6">
        <h2 className="lb-eyebrow">
          {currentLanguage === 'ko' ? '목차' : 'Contents'}
        </h2>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <nav className="space-y-1">
          {sections.map((section) => {
            const Icon = sectionIcons[section.id];
            const isActive = activeSection === section.id;

            return (
              <div key={section.id}>
                <button
                  type="button"
                  onClick={() => onSectionChange(section.id)}
                  className={`w-full flex items-center gap-2.5 rounded-lg py-2.5 px-3 text-left transition-all duration-150 ${
                    isActive
                      ? 'bg-indigo-500/10 text-indigo-300 border border-indigo-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.04] border border-transparent'
                  }`}
                >
                  {Icon && (
                    <Icon
                      className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-indigo-400' : ''}`}
                      strokeWidth={1.5}
                    />
                  )}
                  <span className={`flex-1 text-sm font-medium ${isActive ? 't-strong' : ''}`}>
                    {section.label}
                  </span>
                  <ChevronRight
                    strokeWidth={1.5}
                    className={`w-3.5 h-3.5 transition-transform ${isActive ? 'rotate-90 text-indigo-400' : ''}`}
                  />
                </button>

                {isActive && section.subsections.length > 0 && (
                  <div className="ml-2 mt-1 space-y-0.5 border-l border-white/[0.06] pl-2">
                    {section.subsections.map((subsection) => {
                      const isSubActive = activeSubsection === subsection.id;
                      return (
                        <button
                          key={subsection.id}
                          type="button"
                          onClick={() => onSubsectionChange(subsection.id)}
                          className={`w-full rounded-md py-2 px-3 text-sm text-left transition-colors ${
                            isSubActive
                              ? 'bg-indigo-500/10 text-indigo-300 font-medium'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-white/[0.03]'
                          }`}
                        >
                          {subsection.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </div>
  );
};
